/**
 * Detector de source `type: 'link'`.
 *
 * Event delegation em `document` (capture). Nunca chama preventDefault
 * nem altera navegação. Tracking ocorre também com meta/ctrl (nova aba).
 *
 * Política disabled: ignora se o matched element ou o anchor tiverem
 * `disabled` / `aria-disabled="true"`.
 */

import type { LinkAutoSource } from '@/config/shared/auto-source';
import type { SelectorType } from '@/config/shared/selector';
import { DEFAULT_SELECTOR_TYPE } from '@/config/shared/selector';
import type { PixelRuntimeContext } from '@/runtime/PixelRuntimeContext';

import { BaseAutoTracker } from '../../auto/BaseAutoTracker';
import { buildAutoSourceOrigin } from '../../auto/build-auto-source-origin';
import {
  isLinkSupportedEvent,
  mapLinkBindingsToEvent,
} from '../../auto/link/LinkAutoEventMapper';
import {
  formatMatchedPathForDiagnostics,
  matchLinkTarget,
} from '../../auto/link/LinkTargetMatcher';
import { resolveLinkBindings } from '../../auto/link/LinkValueResolver';
import { normalizeLinkAutoSource } from '../../auto/link/normalizeLinkAutoSource';
import type {
  ConversionEventOrigin,
  ConversionEventType,
} from '../../contracts';
import { SelectorResolver } from '../SelectorResolver';
import type { TrackerFactory } from '../../tracks/TrackerFactory';

export type ConfiguredLinkOptions = {
  readonly eventType: ConversionEventType;
  readonly source: LinkAutoSource;
  readonly origin?: ConversionEventOrigin;
};

export type LinkDetectorDebugSnapshot = {
  readonly sourceName: string;
  readonly sourceType: 'link';
  readonly eventType: ConversionEventType;
  readonly enabled: boolean;
  readonly initialized: boolean;
  readonly matches: number;
  readonly ignoredClicks: number;
  readonly invalidUrl: number;
  readonly unsupportedProtocol: number;
  readonly missingRequired: number;
  readonly mapperRejected: number;
  readonly fired: number;
  readonly fireOnceBlocked: number;
  readonly debounceBlocked: number;
  readonly cooldownBlocked: number;
  readonly lastIgnoredReason: string | null;
  readonly lastResolvedKeys: readonly string[];
  readonly lastHostname: string | null;
  /** Path da regra de match (nunca o pathname real do usuário). */
  readonly lastPathname: string | null;
};

const SUPPORTED_PROTOCOLS = new Set(['http:', 'https:']);

function isDisabled(el: Element): boolean {
  if (el.hasAttribute('disabled')) return true;
  if (el.getAttribute('aria-disabled') === 'true') return true;
  return false;
}

function findAnchor(from: Element): HTMLAnchorElement | null {
  if (
    typeof HTMLAnchorElement !== 'undefined' &&
    from instanceof HTMLAnchorElement &&
    from.hasAttribute('href')
  ) {
    return from;
  }
  const closest = from.closest('a[href]');
  return closest instanceof HTMLAnchorElement ? closest : null;
}

/**
 * @deprecated Prefer `formatMatchedPathForDiagnostics` (regra de match).
 * Fallback: redige todos os segmentos — nunca preserva literais do pathname.
 */
export function sanitizePathnameForDiagnostics(pathname: string): string {
  const trimmed = pathname.trim();
  if (!trimmed || trimmed === '/') return '/';
  const withLeading = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  const normalized =
    withLeading.length > 1 && withLeading.endsWith('/')
      ? withLeading.slice(0, -1)
      : withLeading;
  if (normalized === '/') return '/';
  return (
    '/' +
    normalized
      .split('/')
      .filter(Boolean)
      .map(() => ':redacted')
      .join('/')
  );
}

/**
 * Normaliza href absoluto a partir do baseURI do documento.
 */
export function normalizeLinkHref(href: string, baseURI: string): URL | null {
  try {
    const url = new URL(href, baseURI);
    if (!SUPPORTED_PROTOCOLS.has(url.protocol)) {
      return null;
    }
    return url;
  } catch {
    return null;
  }
}

export class ConfiguredLinkDetector extends BaseAutoTracker {
  private readonly factory: TrackerFactory;

  private readonly eventType: ConversionEventType;

  private readonly source: LinkAutoSource;

  private readonly inactiveReason: string | null;

  private readonly resolver: SelectorResolver;

  private readonly selectorType: SelectorType;

  private readonly fireOnce: boolean;

  private readonly debounceMs: number;

  private readonly cooldownMs: number;

  private readonly origin: ConversionEventOrigin;

  private hasFired = false;

  private destroyed = false;

  private initialized = false;

  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  private lastFireAt = 0;

  private readonly stats = {
    matches: 0,
    ignoredClicks: 0,
    invalidUrl: 0,
    unsupportedProtocol: 0,
    missingRequired: 0,
    mapperRejected: 0,
    fired: 0,
    fireOnceBlocked: 0,
    debounceBlocked: 0,
    cooldownBlocked: 0,
    lastIgnoredReason: null as string | null,
    lastResolvedKeys: [] as string[],
    lastHostname: null as string | null,
    lastPathname: null as string | null,
  };

  constructor(
    context: PixelRuntimeContext,
    factory: TrackerFactory,
    config: ConfiguredLinkOptions
  ) {
    super(context);
    this.factory = factory;
    this.eventType = config.eventType;

    const normalized = normalizeLinkAutoSource(config.source);
    if (!normalized.ok) {
      this.inactiveReason = normalized.reason;
      // Placeholder mínimo — listener não será instalado (fail closed).
      this.source = {
        name: config.source.name?.trim() || 'invalid-link',
        type: 'link',
        match: { path: { exact: ['/__invalid_link_source__'] } },
      };
      this.context.logger.debug(
        `[ConfiguredLinkDetector] inactive: ${normalized.reason}`
      );
    } else {
      this.inactiveReason = null;
      this.source = normalized.source;
    }

    this.selectorType = this.source.selectorType ?? DEFAULT_SELECTOR_TYPE;
    this.fireOnce = this.source.fireOnce === true;
    this.debounceMs =
      typeof this.source.debounceMs === 'number' &&
      Number.isFinite(this.source.debounceMs) &&
      this.source.debounceMs > 0
        ? this.source.debounceMs
        : 0;
    this.cooldownMs =
      typeof this.source.cooldownMs === 'number' &&
      Number.isFinite(this.source.cooldownMs) &&
      this.source.cooldownMs > 0
        ? this.source.cooldownMs
        : 0;
    this.origin =
      config.origin ??
      buildAutoSourceOrigin('link', config.eventType, this.source.name);
    this.resolver = new SelectorResolver(context.logger);
  }

  get name(): string {
    return `ConfiguredLink(${this.eventType}:${this.source.name})`;
  }

  get isActive(): boolean {
    return this.inactiveReason === null && this.source.enabled !== false;
  }

  getDebugSnapshot(): LinkDetectorDebugSnapshot {
    return {
      sourceName: this.source.name,
      sourceType: 'link',
      eventType: this.eventType,
      enabled: this.isActive,
      initialized: this.initialized,
      matches: this.stats.matches,
      ignoredClicks: this.stats.ignoredClicks,
      invalidUrl: this.stats.invalidUrl,
      unsupportedProtocol: this.stats.unsupportedProtocol,
      missingRequired: this.stats.missingRequired,
      mapperRejected: this.stats.mapperRejected,
      fired: this.stats.fired,
      fireOnceBlocked: this.stats.fireOnceBlocked,
      debounceBlocked: this.stats.debounceBlocked,
      cooldownBlocked: this.stats.cooldownBlocked,
      lastIgnoredReason: this.inactiveReason ?? this.stats.lastIgnoredReason,
      lastResolvedKeys: [...this.stats.lastResolvedKeys],
      lastHostname: this.stats.lastHostname,
      lastPathname: this.stats.lastPathname,
    };
  }

  protected onInit(): void {
    this.destroyed = false;
    this.hasFired = false;
    this.lastFireAt = 0;
    this.initialized = true;
    if (this.inactiveReason) return;
    if (this.source.enabled === false) return;
    if (!this.isDocumentAvailable()) return;
    if (!isLinkSupportedEvent(this.eventType)) return;
    this.addDocumentListener('click', event => this.handleClick(event), true);
  }

  protected onDestroy(): void {
    this.destroyed = true;
    this.initialized = false;
    this.debounceTimer = this.clearManagedTimeout(this.debounceTimer);
  }

  private ignore(reason: string): void {
    this.stats.ignoredClicks += 1;
    this.stats.lastIgnoredReason = reason;
  }

  private handleClick(event: MouseEvent): void {
    if (this.destroyed || this.inactiveReason) return;
    if (this.source.enabled === false) return;
    if (this.fireOnce && this.hasFired) {
      this.stats.fireOnceBlocked += 1;
      return;
    }

    try {
      if (typeof event.button === 'number' && event.button !== 0) {
        this.ignore('non-primary-button');
        return;
      }

      const resolved = this.resolveClickElements(event);
      if (!resolved) {
        this.ignore('no-match');
        return;
      }
      const { anchor, target } = resolved;

      if (isDisabled(target) || isDisabled(anchor)) {
        this.ignore('disabled');
        return;
      }

      const href = anchor.getAttribute('href');
      if (!href || href.trim() === '') {
        this.ignore('missing-href');
        return;
      }

      const lower = href.trim().toLowerCase();
      if (
        lower.startsWith('mailto:') ||
        lower.startsWith('tel:') ||
        lower.startsWith('javascript:')
      ) {
        this.stats.unsupportedProtocol += 1;
        this.ignore('unsupported-protocol');
        return;
      }

      const baseURI =
        typeof document !== 'undefined' && document.baseURI
          ? document.baseURI
          : 'https://localhost/';
      const url = normalizeLinkHref(href, baseURI);
      if (!url) {
        this.stats.invalidUrl += 1;
        this.ignore('invalid-url');
        return;
      }

      if (!SUPPORTED_PROTOCOLS.has(url.protocol)) {
        this.stats.unsupportedProtocol += 1;
        this.ignore('unsupported-protocol');
        return;
      }

      const matchResult = matchLinkTarget(url, this.source.match);
      if (!matchResult.matched) {
        this.ignore('target-mismatch');
        return;
      }

      this.stats.matches += 1;
      this.stats.lastHostname = url.hostname;
      this.stats.lastPathname = formatMatchedPathForDiagnostics(
        matchResult.matchedPath
      );

      const resolution = resolveLinkBindings(this.source.bindings, {
        anchor,
        target,
        url,
        pathParams: matchResult.pathParams,
      });

      this.stats.lastResolvedKeys = Object.keys(resolution.values);

      if (!resolution.valid) {
        this.stats.missingRequired += 1;
        this.ignore(`missing-required:${resolution.missingRequired.join(',')}`);
        this.context.logger.debug(
          `[ConfiguredLinkDetector] ${this.source.name} missing required bindings`,
          resolution.missingRequired
        );
        return;
      }

      const mapped = mapLinkBindingsToEvent(this.eventType, resolution.values);
      if (!mapped.ok) {
        this.stats.mapperRejected += 1;
        this.ignore(`mapper:${mapped.reason}`);
        this.context.logger.debug(
          `[ConfiguredLinkDetector] ${this.source.name} mapper rejected: ${mapped.reason}`
        );
        return;
      }

      if (mapped.unknownKeys?.length) {
        this.context.logger.debug(
          `[ConfiguredLinkDetector] ${this.source.name} unknown binding keys ignored`,
          mapped.unknownKeys
        );
      }

      this.scheduleFire(mapped.data);
    } catch (error) {
      this.ignore('exception');
      this.context.logger.debug(
        `[ConfiguredLinkDetector] ${this.eventType} ignored:`,
        error
      );
    }
  }

  private scheduleFire(data: Parameters<TrackerFactory['trackAuto']>[1]): void {
    const now = Date.now();
    if (this.cooldownMs > 0 && now - this.lastFireAt < this.cooldownMs) {
      this.stats.cooldownBlocked += 1;
      return;
    }

    if (this.debounceMs > 0) {
      this.stats.debounceBlocked += 1;
      this.debounceTimer = this.clearManagedTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.debounceTimer = null;
        this.fire(data);
      }, this.debounceMs);
      return;
    }

    this.fire(data);
  }

  private fire(data: Parameters<TrackerFactory['trackAuto']>[1]): void {
    if (this.destroyed || this.inactiveReason) return;
    if (this.source.enabled === false) return;
    if (this.fireOnce && this.hasFired) {
      this.stats.fireOnceBlocked += 1;
      return;
    }

    this.factory.trackAuto(this.eventType, data, { origin: this.origin });
    this.stats.fired += 1;
    this.lastFireAt = Date.now();
    if (this.fireOnce) {
      this.hasFired = true;
    }
  }

  /**
   * 1) Resolve o `<a href>` a partir do event.target
   * 2) Se houver selector, exige que o match esteja no anchor, no target
   *    do clique ou em um ancestral do anchor (container).
   */
  private resolveClickElements(
    event: Event
  ): { anchor: HTMLAnchorElement; target: Element } | null {
    const clickTarget = event.target instanceof Element ? event.target : null;
    if (!clickTarget) return null;

    const anchor = findAnchor(clickTarget);
    if (!anchor) return null;

    if (!this.source.selector) {
      return { anchor, target: anchor };
    }

    // Candidatos: click target → ancestrais até document; inclui o anchor.
    const candidates: Element[] = [];
    let current: Element | null = clickTarget;
    while (current && current !== document.documentElement) {
      candidates.push(current);
      current = current.parentElement;
    }
    if (!candidates.includes(anchor)) {
      candidates.unshift(anchor);
    }

    for (const element of candidates) {
      if (
        this.resolver.matches(element, this.source.selector, this.selectorType)
      ) {
        return { anchor, target: element };
      }
    }

    return null;
  }
}
